import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyForm = {
  code: '',
  name: '',
  discount_type: 'PERCENTAGE',
  discount_value: '10',
  min_order_amount: '0',
  max_discount_amount: '',
  inventory_ids: [],
  is_active: true,
  valid_from: '',
  valid_until: '',
  usage_limit: '',
};

/**
 * Admin-configurable cart promo coupons (code + % or fixed ₹).
 * Multi-hub: empty selection = all hubs.
 */
export function PromoCoupons() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const hubOptions = useMemo(
    () =>
      inventoryList.map((h) => ({
        value: Number(h.value ?? h.id),
        name: h.label || h.place || h.name || `Hub #${h.value ?? h.id}`,
      })),
    [inventoryList]
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    dispatch(updatepageName('promo_coupons'));
    fetchCoupons();
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubsLabel = (row) => {
    const ids = row.inventory_ids || [];
    if (!ids.length) return 'All hubs';
    const places = row.inventory_places || [];
    if (places.length) {
      return places.length <= 3
        ? places.join(', ')
        : `${places.slice(0, 3).join(', ')} (+${places.length - 3})`;
    }
    return ids
      .map((id) => {
        const hub = hubOptions.find((h) => Number(h.value) === Number(id));
        return hub?.name || `Hub #${id}`;
      })
      .join(', ');
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await CustomAxios.get('promo-coupons/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setCoupons(data);
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.detail || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditMode(false);
    setSelectedId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelectedId(row.id);
    setForm({
      code: row.code || '',
      name: row.name || '',
      discount_type: row.discount_type || 'PERCENTAGE',
      discount_value: String(row.discount_value ?? '0'),
      min_order_amount: String(row.min_order_amount ?? '0'),
      max_discount_amount: row.max_discount_amount != null ? String(row.max_discount_amount) : '',
      inventory_ids: (row.inventory_ids || []).map(Number),
      is_active: Boolean(row.is_active),
      valid_from: row.valid_from ? String(row.valid_from).slice(0, 16) : '',
      valid_until: row.valid_until ? String(row.valid_until).slice(0, 16) : '',
      usage_limit: row.usage_limit != null ? String(row.usage_limit) : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      showToast('warn', 'Validation', 'Code and name are required');
      return;
    }
    if (!(Number(form.discount_value) > 0)) {
      showToast('warn', 'Validation', 'Discount value must be greater than 0');
      return;
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value || 0),
      min_order_amount: Number(form.min_order_amount || 0),
      max_discount_amount: form.max_discount_amount === '' ? null : Number(form.max_discount_amount),
      inventory_ids: (form.inventory_ids || []).map(Number),
      is_active: Boolean(form.is_active),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      usage_limit: form.usage_limit === '' ? null : Number(form.usage_limit),
    };

    try {
      setSaving(true);
      if (editMode && selectedId) {
        await CustomAxios.patch(`promo-coupons/${selectedId}/`, payload);
        showToast('success', 'Saved', 'Coupon updated');
      } else {
        await CustomAxios.post('promo-coupons/', payload);
        showToast('success', 'Created', 'Coupon created');
      }
      setModalOpen(false);
      fetchCoupons();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.code?.[0] ||
        err?.response?.data?.inventory_ids?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        JSON.stringify(err?.response?.data || {}) ||
        'Save failed';
      showToast('error', 'Error', String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Toast ref={toast} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5">Promo Coupons</Typography>
          <Typography variant="body2" color="text.secondary">
            Cart coupons: multi-select hubs (leave empty for all hubs). Mobile Apply uses these.
          </Typography>
        </Box>
        <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreate} disabled={loading}>
          Add Coupon
        </GrowthPrimaryButton>
      </Box>

      {loading ? <GrowthPageLoader label="Loading coupons…" /> : null}
      {!loading && (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Min order</TableCell>
              <TableCell>Hubs</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Used</TableCell>
              <TableCell align="right">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.discount_type}</TableCell>
                <TableCell>{row.discount_value}</TableCell>
                <TableCell>{row.min_order_amount}</TableCell>
                <TableCell>{hubsLabel(row)}</TableCell>
                <TableCell>{row.is_active ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {row.used_count}
                  {row.usage_limit != null ? ` / ${row.usage_limit}` : ''}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(row)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && coupons.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No coupons yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editMode ? 'Edit Coupon' : 'Create Coupon'}
        subtitle="Cart promo codes — percentage or fixed discount"
        busy={saving}
        busyLabel={editMode ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              Save
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Discount type</InputLabel>
                <Select
                  label="Discount type"
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                >
                  <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                  <MenuItem value="FIXED">Fixed amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={form.discount_type === 'PERCENTAGE' ? 'Percentage' : 'Amount (₹)'}
                type="number"
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Min order amount"
                type="number"
                value={form.min_order_amount}
                onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Max discount (optional)"
                type="number"
                value={form.max_discount_amount}
                onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={hubOptions}
                getOptionLabel={(option) => option.name}
                value={hubOptions.filter((h) => form.inventory_ids.includes(Number(h.value)))}
                onChange={(_, newValue) =>
                  setForm({
                    ...form,
                    inventory_ids: newValue.map((h) => Number(h.value)),
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Hubs (optional)"
                    placeholder="Leave empty for all hubs"
                    helperText="Multi-select. Empty = coupon valid on every hub."
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option.name}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.value}
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
                limitTags={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Usage limit (optional)"
                type="number"
                value={form.usage_limit}
                onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Valid from"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.valid_from}
                onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Valid until"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}
